import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Download, Upload, Loader2, Layers, Search, Ban, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const STATUS_COLORS = {
  imported: "bg-sky-100 text-sky-800", assigned: "bg-indigo-100 text-indigo-800",
  letter_generated: "bg-slate-200 text-slate-700", submitted_to_mail: "bg-amber-100 text-amber-800",
  mailed: "bg-amber-200 text-amber-900", qr_scanned: "bg-cyan-100 text-cyan-800",
  form_completed: "bg-cyan-200 text-cyan-900", lead: "bg-emerald-100 text-emerald-800",
  booking: "bg-emerald-200 text-emerald-900", inspection: "bg-emerald-600 text-white",
  water_test: "bg-emerald-700 text-white", customer: "bg-emerald-800 text-white",
  do_not_mail: "bg-red-100 text-red-800", invalid_address: "bg-red-100 text-red-700",
  address_conflict: "bg-amber-100 text-amber-900", excluded: "bg-slate-200 text-slate-500",
};

export const StatusChip = ({ status }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_COLORS[status] || "bg-slate-100 text-slate-600"}`}>
    {(status || "").replaceAll("_", " ")}
  </span>
);

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function AddressDatabase() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({ q: "", suburb: "", campaign_group: "", status: "", water_confidence: "", mailed: "", assigned: "", do_not_mail: "" });
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ street_address: "", suburb: "", state: "QLD", postcode: "", water_confidence: "" });
  const [busy, setBusy] = useState(false);
  const [qInput, setQInput] = useState("");
  const reqSeq = useRef(0);
  const navigate = useNavigate();

  // debounce search input → filters.q
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setFilters((f) => (f.q === qInput ? f : { ...f, q: qInput })); }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page, page_size: 50 });
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    const seq = ++reqSeq.current;
    api.get(`/admin/address-database?${params}`)
      .then(({ data: d }) => { if (seq === reqSeq.current) setData(d); })
      .catch(() => {});
  }, [filters, page]);
  useEffect(load, [load]);
  useEffect(() => { api.get("/admin/address-database/analytics").then(({ data: d }) => setAnalytics(d)).catch(() => {}); }, []);

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };

  const manualAdd = async (force = false) => {
    setBusy(true);
    try {
      await api.post("/admin/address-database", { ...addForm, force_keep_both: force });
      toast.success("Property added");
      setShowAdd(false); setAddForm({ street_address: "", suburb: "", state: "QLD", postcode: "", water_confidence: "" });
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Add failed";
      if (msg.includes("conflict") && !force && window.confirm(`${msg}\n\nAdd anyway as a distinct property?`)) return manualAdd(true);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("scwt_token");
    const params = new URLSearchParams();
    ["suburb", "campaign_group", "mailed"].forEach((k) => filters[k] && params.set(k, filters[k]));
    fetch(`${api.defaults.baseURL}/admin/address-database/export?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = "aware-address-database.csv"; a.click();
      }).catch(() => toast.error("Export failed"));
  };

  const sel = "rounded-lg border border-slate-300 text-sm px-2 py-1.5 bg-white";
  const f = data?.filters || {};

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="address-database-page">
      <Link to="/admin/property-campaigns" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Property Campaigns</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
        <h1 className="text-2xl font-bold text-slate-900">Address Database</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/address-database/groups"><Button variant="outline" className="rounded-full" data-testid="adb-groups-link"><Layers className="w-4 h-4 mr-1" /> Address Groups</Button></Link>
          <Link to="/admin/address-database/import"><Button className="rounded-full bg-slate-900" data-testid="adb-import-link"><Upload className="w-4 h-4 mr-1" /> Import Addresses</Button></Link>
          <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="adb-export-btn"><Download className="w-4 h-4 mr-1" /> Export</Button>
          <Button variant="outline" className="rounded-full" onClick={() => setShowAdd(!showAdd)} data-testid="adb-add-btn"><Plus className="w-4 h-4 mr-1" /> Add Property</Button>
        </div>
      </div>

      {analytics && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2.5">
          {[["Total", analytics.total_properties], ["Assigned", analytics.assigned], ["Mailed", analytics.mailed],
            ["Never Mailed", analytics.never_mailed], ["Leads", analytics.leads], ["Do Not Mail", analytics.do_not_mail]].map(([l, v]) => (
            <div key={l} className="rounded-2xl bg-white border border-slate-200 p-3" data-testid={`adb-stat-${l.toLowerCase().replaceAll(" ", "-")}`}>
              <div className="text-xl font-bold text-slate-900">{v}</div>
              <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">{l}</div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-4" data-testid="adb-add-form">
          <div className="font-semibold text-sm text-slate-900 mb-2">Add property (same duplicate/conflict checks as import)</div>
          <div className="flex flex-wrap gap-2">
            <input className={`${sel} w-56`} placeholder="Street address" value={addForm.street_address} onChange={(e) => setAddForm({ ...addForm, street_address: e.target.value })} data-testid="adb-add-street" />
            <input className={`${sel} w-36`} placeholder="Suburb" value={addForm.suburb} onChange={(e) => setAddForm({ ...addForm, suburb: e.target.value })} data-testid="adb-add-suburb" />
            <select className={sel} value={addForm.state} onChange={(e) => setAddForm({ ...addForm, state: e.target.value })}>
              {["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <input className={`${sel} w-24`} placeholder="Postcode" value={addForm.postcode} onChange={(e) => setAddForm({ ...addForm, postcode: e.target.value })} data-testid="adb-add-postcode" />
            <select className={sel} value={addForm.water_confidence} onChange={(e) => setAddForm({ ...addForm, water_confidence: e.target.value })}>
              <option value="">Water confidence…</option><option>High</option><option>To Verify</option>
            </select>
            <Button disabled={busy} onClick={() => manualAdd(false)} className="rounded-full bg-slate-900" data-testid="adb-add-save">Save</Button>
          </div>
        </div>
      )}

      <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-3 flex flex-wrap items-center gap-2" data-testid="adb-filters">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input className={`${sel} pl-8 w-56`} placeholder="Search address / postcode…" value={qInput}
            onChange={(e) => setQInput(e.target.value)} data-testid="adb-search" />
        </div>
        <select className={sel} value={filters.suburb} onChange={(e) => setF("suburb", e.target.value)} data-testid="adb-filter-suburb">
          <option value="">All suburbs</option>{(f.suburbs || []).map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className={sel} value={filters.campaign_group} onChange={(e) => setF("campaign_group", e.target.value)} data-testid="adb-filter-group">
          <option value="">All campaigns</option>{(f.campaign_groups || []).map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className={sel} value={filters.status} onChange={(e) => setF("status", e.target.value)} data-testid="adb-filter-status">
          <option value="">All statuses</option>{(f.statuses || []).map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
        </select>
        <select className={sel} value={filters.water_confidence} onChange={(e) => setF("water_confidence", e.target.value)}>
          <option value="">Any confidence</option>{(f.water_confidences || []).map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className={sel} value={filters.mailed} onChange={(e) => setF("mailed", e.target.value)} data-testid="adb-filter-mailed">
          <option value="">Mailed + unmailed</option><option value="yes">Mailed</option><option value="no">Never mailed</option>
        </select>
        <select className={sel} value={filters.assigned} onChange={(e) => setF("assigned", e.target.value)}>
          <option value="">Assigned + unassigned</option><option value="yes">Assigned</option><option value="no">Unassigned</option>
        </select>
      </div>

      <div className="mt-4 rounded-2xl bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <th className="px-4 py-2.5">Address</th><th className="px-2 py-2.5">Confidence</th>
            <th className="px-2 py-2.5">Campaign</th><th className="px-2 py-2.5">Last Mailed</th>
            <th className="px-2 py-2.5">×Mailed</th><th className="px-2 py-2.5">Status</th><th className="px-2 py-2.5"></th>
          </tr></thead>
          <tbody>
            {(data?.items || []).map((p) => (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`adb-row-${p.id}`}
                onClick={() => navigate(`/admin/address-database/property/${p.id}`)}>
                <td className="px-4 py-2.5 font-medium text-slate-900">{p.street_address}, {p.suburb} {p.postcode}
                  {(p.conflict || {}).status === "unresolved" && <AlertTriangle className="inline w-3.5 h-3.5 text-amber-500 ml-1.5" />}
                </td>
                <td className="px-2 py-2.5 text-slate-600">{p.water_confidence || "—"}</td>
                <td className="px-2 py-2.5 font-mono text-xs text-slate-600">{p.campaign_group || "—"}</td>
                <td className="px-2 py-2.5 text-slate-600">{fmtD(p.last_mailed_at)}</td>
                <td className="px-2 py-2.5 text-slate-600">{p.times_mailed || 0}</td>
                <td className="px-2 py-2.5"><StatusChip status={p.status} /></td>
                <td className="px-2 py-2.5">
                  {p.do_not_mail && <Ban className="w-4 h-4 text-red-500" />}
                  {(p.lead_invite_ids || []).length > 0 && <span className="text-[10px] font-bold text-emerald-700 ml-1">LEAD</span>}
                  {p.customer_id && <span className="text-[10px] font-bold text-emerald-800 ml-1">CUSTOMER</span>}
                </td>
              </tr>
            ))}
            {data && !data.items.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No properties match. Import a spreadsheet to begin.</td></tr>}
          </tbody>
        </table>
      </div>
      {data && data.pages > 1 && (
        <div className="mt-3 flex items-center gap-3 text-sm text-slate-600" data-testid="adb-pagination">
          <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button>
          Page {data.page} of {data.pages} · {data.total} properties
          <Button variant="outline" size="sm" className="rounded-full" disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
      {!data && <div className="p-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>}
    </div>
  );
}
